import type { IVideoSrcParserOptions } from "../types";
import { parseOpts } from "./parseOpts";

/**
 * Unlisted Vimeo videos are only embeddable when the player URL carries their
 * privacy hash. Vimeo exposes it either as the segment after the video id
 * (`vimeo.com/123456789/abcdef1234`) or as an `h` query param
 * (`vimeo.com/123456789?h=abcdef1234`). The path form only accepts a
 * hash-shaped token that ends at a path/query boundary, so a non-hash trailing
 * segment (or a longer segment that merely starts with hex characters) is never
 * mistaken for one and cannot break an otherwise public video.
 */
export const getVimeoPrivacyHash = (url: string) => {
  const pathMatch = url.match(
    /vimeo\.com\/(?:video\/)?\d+\/([0-9a-f]{6,})(?=[/?#]|$)/,
  );
  if (pathMatch) return pathMatch[1];

  const queryMatch = url.match(/[?&]h=([a-zA-Z0-9]+)/);
  if (queryMatch) return queryMatch[1];

  return "";
};

export const getVimeoEmbedSrc = (
  url: string,
  opts: IVideoSrcParserOptions = {},
  isPdf = false,
) => {
  const baseUrl = "https://player.vimeo.com/video/";
  const regex = /vimeo\.com\/(video\/)?(\d+)/;
  const match = url.match(regex);

  let result = "";
  if (match && match.length > 1) {
    const videoId = match[match.length - 1];
    result = baseUrl + videoId;
  }

  const { controls, autoplay, loop } = parseOpts(opts, isPdf);

  result = result.replace(
    /[&?]?(muted|autopause|controls|autoplay|loop)=\d/gm,
    "",
  );
  const s: string[] = [];
  const privacyHash = getVimeoPrivacyHash(url);
  if (privacyHash) s.push(`h=${privacyHash}`);
  s.push(`controls=${controls ? 1 : 0}`);
  if (autoplay) {
    s.push(`autoplay=1&autopause=0`);
  } else s.push(`autoplay=0`);
  s.push(`loop=${loop ? 1 : 0}`);
  s.push("muted=1");

  return result + `?${s.join("&")}`;
};
